import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../../components/header';
import { useLanguage } from '../../../helpers/language-context';
import { supabase } from '../../../supabaseClient';

const OurStoriesDetail = () => {
    const { translations, language } = useLanguage();
    const { storyId } = useParams();
    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStory = async () => {
            if (!storyId) return;

            const { data, error } = await supabase
                .from('our_stories')
                .select('*')
                .eq('id', storyId)
                .single();

            if (error) {
                console.error('Error fetching story:', error);
            } else {
                setStory(data);
            }
            setLoading(false);
        };

        fetchStory();
    }, [storyId]);

    const getLocalized = (item, field) => {
        if (!item) return '';
        return language === 'ms'
            ? (item[`${field}_ms`] || item[`${field}_en`])
            : (item[`${field}_en`] || item[`${field}_ms`]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString(language === 'ms' ? 'ms-MY' : 'en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#28357B]"></div>
            </div>
        );
    }

    if (!story) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center space-y-4">
                <h2 className="text-2xl font-bold text-gray-700">Story not found</h2>
                <Link to="/media/our-stories" className="text-[#28357B] hover:underline">Back to Our Stories</Link>
            </div>
        );
    }

    return (
        <div className="bg-white min-h-screen">
            <Header
                title={getLocalized(story, 'title')}
                back={true}
                backLink='/media/our-stories'
                backText={translations.media.ourStories || 'Our Stories'}
            >
                <div className="flex flex-col-reverse md:flex-row justify-center md:justify-between items-center mt-6 md:mt-[10vh] gap-6 md:gap-0">
                    {/* Social Media Links */}
                    <div className="flex items-center gap-6">
                        {/* Facebook */}
                        <button type="button" className="text-white hover:text-gray-300 transition-colors">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                        </button>
                        {/* Instagram */}
                        <button type="button" className="text-white hover:text-gray-300 transition-colors">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>
                        </button>
                        {/* X (Twitter) */}
                        <button type="button" className="text-white hover:text-gray-300 transition-colors">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                        </button>
                        {/* LinkedIn */}
                        <button type="button" className="text-white hover:text-gray-300 transition-colors">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" /></svg>
                        </button>
                    </div>
                    <div className="text-white text-sm font-medium">
                        {formatDate(story.date)}
                    </div>
                </div>
            </Header>

            <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
                <article className="space-y-8">
                    {/* Cover Image */}
                    {story.cover_image_url && (
                        <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg">
                            <img
                                src={story.cover_image_url}
                                alt={getLocalized(story, 'title')}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Description 1 */}
                    {getLocalized(story, 'description1') && (
                        <div className="prose prose-lg text-gray-700 max-w-none whitespace-pre-wrap">
                            {getLocalized(story, 'description1')}
                        </div>
                    )}

                    {/* Secondary Image */}
                    {story.stories_image_url && (
                        <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg mt-8">
                            <img
                                src={story.stories_image_url}
                                alt="Story detail"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Description 2 */}
                    {getLocalized(story, 'description2') && (
                        <div className="prose prose-lg text-gray-700 max-w-none whitespace-pre-wrap mt-8">
                            {getLocalized(story, 'description2')}
                        </div>
                    )}
                </article>
            </div>
        </div>
    );
}

export default OurStoriesDetail;
